// Ask Arif — knowledge base data layer.
// Designed to scale to 100–300 articles. Each article has full SEO + content structure.

import {
  Shield,
  Radar,
  Cpu,
  Film,
  Speaker,
  Building2,
  Sparkles,
  GraduationCap,
  Wallet,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";

export type AskArifCategory = {
  slug: string;
  title: string;
  short: string;
  Icon: LucideIcon;
  accent: string; // tailwind gradient class
};

export const ASK_ARIF_CATEGORIES: AskArifCategory[] = [
  { slug: "security-infrastructure", title: "Security Infrastructure", short: "Enterprise-grade hardening, surveillance and access control.", Icon: Shield, accent: "from-primary/20 to-accent/10" },
  { slug: "perimeter-protection", title: "Perimeter Protection", short: "Multi-layer detection across kilometres of perimeter.", Icon: Radar, accent: "from-primary/25 to-transparent" },
  { slug: "smart-automation", title: "Smart Automation", short: "Unified intelligence for buildings that respond to people.", Icon: Cpu, accent: "from-accent/20 to-primary/10" },
  { slug: "luxury-home-cinema", title: "Luxury Home Cinema", short: "Reference-grade private theatres, acoustically engineered.", Icon: Film, accent: "from-primary/20 to-accent/15" },
  { slug: "audio-video", title: "Audio Video", short: "Boardrooms, auditoriums and integrated AV at scale.", Icon: Speaker, accent: "from-primary/15 to-accent/10" },
  { slug: "intelligent-buildings", title: "Intelligent Buildings", short: "BMS, IBMS and the operating model for modern facilities.", Icon: Building2, accent: "from-accent/20 to-primary/15" },
  { slug: "technology-trends", title: "Technology Trends", short: "Edge AI, sensor fusion and what's actually shipping next.", Icon: Sparkles, accent: "from-primary/20 to-accent/20" },
  { slug: "project-learnings", title: "Project Learnings", short: "Patterns from 1000+ projects and 500km+ of perimeter.", Icon: GraduationCap, accent: "from-accent/15 to-primary/10" },
  { slug: "cost-guides", title: "Cost Guides", short: "Honest, range-based budgets without vendor spin.", Icon: Wallet, accent: "from-primary/15 to-accent/15" },
  { slug: "mistakes-to-avoid", title: "Mistakes To Avoid", short: "The avoidable design errors that wreck great builds.", Icon: AlertTriangle, accent: "from-accent/25 to-primary/10" },
];

export type AskArifArticle = {
  slug: string;
  category: string; // category slug
  question: string;
  shortAnswer: string;
  readingMinutes: number;
  publishedAt: string; // ISO
  trending?: boolean;
  popular?: boolean;
  recommended?: boolean;
  tags: string[];
  visual: string; // descriptive text used as a "visual explanation" placeholder
  example: string;
  lessons: string[];
  relatedProjects: string[];
  faq: { q: string; a: string }[];
};

export const ASK_ARIF_ARTICLES: AskArifArticle[] = [
  {
    slug: "how-much-does-home-automation-cost",
    category: "cost-guides",
    question: "How much does home automation cost in India?",
    shortAnswer:
      "A genuine, integrated smart home in India typically ranges from ₹8–15 lakh for a 3 BHK apartment, ₹25–60 lakh for a luxury villa, and ₹1 crore+ for a fully orchestrated estate. The number depends on the control fabric, the count of automated circuits, lighting scenes, climate zones, AV rooms and the level of acoustic and structural prep done during civil works.",
    readingMinutes: 8,
    publishedAt: "2026-05-10",
    trending: true,
    popular: true,
    recommended: true,
    tags: ["Cost", "Home Automation", "Luxury Living", "Budget"],
    visual:
      "Cost stacks for Apartment, Villa and Estate — broken into Lighting, Climate, Shading, AV, Security and Control Fabric, with relative weight of each layer.",
    example:
      "On a 12,000 sq.ft. Delhi villa we delivered, ~38% of the budget went to the control fabric and structured cabling, ~22% to lighting and shading, ~18% to climate and air, ~14% to AV and cinema, ~8% to security and access. The single biggest swing factor was the owner's decision to commit to a unified control platform during civil works — it cut later rework by an order of magnitude.",
    lessons: [
      "Decide the control fabric before tiling and false ceiling.",
      "Budget for cable pathways, not just devices.",
      "Lighting scenes drive perceived 'smartness' more than any other system.",
      "Plan for a 7–10 year refresh, not a 3 year one.",
    ],
    relatedProjects: ["Luxury villa, South Delhi", "Penthouse, Gurugram"],
    faq: [
      { q: "What is the minimum sensible budget?", a: "Around ₹5–6 lakh for a focused, well-scoped 3 BHK — lighting, shading and a single AV room done properly is better than a thin layer everywhere." },
      { q: "Is it cheaper to retrofit?", a: "Almost never. Retrofitting forces wireless workarounds and visible conduits, and tends to cost 1.4–1.8x of a coordinated new build." },
      { q: "What's the most over-spent line item?", a: "Touch panels. Phones and voice now cover most daily use." },
    ],
  },
  {
    slug: "common-mistakes-in-home-theatre-projects",
    category: "mistakes-to-avoid",
    question: "What mistakes happen in luxury home theatre projects?",
    shortAnswer:
      "The most expensive mistakes are made before any speaker is chosen: wrong room shape, no acoustic shell, no isolation from adjacent walls, HVAC routed straight through the ceiling, and seat geometry that ignores sightlines. A reference home cinema is 60% architecture, 30% acoustics and 10% gear.",
    readingMinutes: 9,
    publishedAt: "2026-04-22",
    trending: true,
    popular: true,
    recommended: true,
    tags: ["Home Cinema", "Acoustics", "Mistakes", "Design"],
    visual:
      "Section drawing of a private theatre with floating floor, decoupled walls, acoustic shell and isolated HVAC paths — versus a typical 'painted black' room.",
    example:
      "A client had already invested in a flagship projector and a high-end speaker brand. The room sounded harsh and bright because rear walls were bare plaster. We added a properly tuned acoustic shell, isolated the ceiling and re-pointed the seating row — same equipment, completely different room.",
    lessons: [
      "Get the shell right before specifying any speaker.",
      "Never let HVAC ducts pass directly through the cinema ceiling.",
      "Lock seat geometry to the screen and reference axis early.",
      "Spend on calibration, not just badges.",
    ],
    relatedProjects: ["Private theatre, Lutyens Delhi", "Atmos cinema, Noida"],
    faq: [
      { q: "Do I need a dedicated room?", a: "For reference performance, yes. Open-plan media rooms trade fidelity for flexibility." },
      { q: "Is Dolby Atmos worth it?", a: "Yes, but only with the correct ceiling height, layout and acoustic prep." },
      { q: "Projector or large OLED?", a: "Projector for cinema scale and ambiance; OLED for daylight rooms." },
    ],
  },
  {
    slug: "how-does-perimeter-security-work",
    category: "perimeter-protection",
    question: "How does perimeter security actually work?",
    shortAnswer:
      "Modern perimeter security is a layered system, not a single fence. It combines physical deterrence, intrusion detection along the line, video verification, command-and-control, and a tested response protocol. The goal is not to stop everyone — it is to detect early, classify accurately and respond before a breach matters.",
    readingMinutes: 10,
    publishedAt: "2026-04-05",
    trending: true,
    popular: true,
    recommended: true,
    tags: ["Perimeter", "Security", "PIDS", "Infrastructure"],
    visual:
      "Layered perimeter diagram: outer deterrence, detection line, verification cameras, command centre, response loop — each labelled with its decision window.",
    example:
      "Across a 38 km industrial perimeter, we segmented the line into 412 zones with fibre-based intrusion detection, paired each zone with PTZ verification and routed alerts through a single PSIM. Average detection-to-verification dropped from 90+ seconds to under 8.",
    lessons: [
      "Detection without verification creates fatigue, not security.",
      "Design the response loop before specifying sensors.",
      "Treat the command centre as a product, not a furniture exercise.",
      "Test the line — quarterly, with real intrusion drills.",
    ],
    relatedProjects: ["38 km industrial perimeter, Western India", "Critical infrastructure, North India"],
    faq: [
      { q: "Is CCTV alone enough?", a: "No. CCTV is verification, not detection." },
      { q: "What is PIDS?", a: "Perimeter Intrusion Detection System — sensors along the perimeter that flag intrusion in real time." },
      { q: "How long does a project like this take?", a: "Typically 6–14 months for a full critical-infrastructure deployment." },
    ],
  },
  {
    slug: "what-is-an-intelligent-building",
    category: "intelligent-buildings",
    question: "What is an intelligent building, really?",
    shortAnswer:
      "An intelligent building is one where security, automation, energy, HVAC, AV and access systems share a common data layer and a single operating model — not a building that has many gadgets. Intelligence is in the integration, not the hardware.",
    readingMinutes: 7,
    publishedAt: "2026-03-18",
    popular: true,
    recommended: true,
    tags: ["IBMS", "BMS", "Intelligent Buildings", "Strategy"],
    visual: "Stack diagram: field layer → controllers → unified data layer → analytics → operator surface.",
    example:
      "A corporate campus we worked on cut energy use 19% in the first year — not from new chillers, but because access, occupancy and HVAC finally spoke the same language.",
    lessons: [
      "Common data layer first, dashboards later.",
      "Pick open protocols wherever the spec allows.",
      "Operations team training matters more than the GUI.",
    ],
    relatedProjects: ["Corporate campus IBMS, North India"],
    faq: [
      { q: "BMS vs IBMS?", a: "BMS controls building services. IBMS adds security, AV, access and analytics into one model." },
      { q: "Is it expensive?", a: "It pays back through energy, operations and risk — usually 3–5 years." },
    ],
  },
  {
    slug: "edge-ai-in-surveillance",
    category: "technology-trends",
    question: "What does edge AI change in surveillance?",
    shortAnswer:
      "Edge AI moves detection from the server room to the camera. That changes everything downstream: bandwidth, storage, alert quality and human workload. The operator stops watching pixels and starts reviewing decisions.",
    readingMinutes: 6,
    publishedAt: "2026-03-02",
    trending: true,
    recommended: true,
    tags: ["AI", "Surveillance", "Edge", "Trends"],
    visual: "Before/after: central VMS doing all analytics vs. edge cameras emitting structured events.",
    example:
      "A logistics yard cut false alarms by 86% after we replaced motion-based recording with edge-classification cameras. Operators went from triaging 400 events a shift to under 60.",
    lessons: [
      "Pick cameras that fit your analytics roadmap, not just resolution.",
      "Plan the VMS around events, not video.",
      "Re-train operators around exception handling.",
    ],
    relatedProjects: ["Logistics yard, Western India"],
    faq: [
      { q: "Does edge AI replace the VMS?", a: "No — it changes what the VMS receives." },
      { q: "Is it future-proof?", a: "More than fixed-rule analytics — but plan firmware lifecycle." },
    ],
  },
  {
    slug: "how-to-budget-a-private-theatre",
    category: "cost-guides",
    question: "How do you budget a luxury private theatre?",
    shortAnswer:
      "A reference private theatre in India typically lands between ₹35 lakh and ₹2.5 crore. The honest split: ~35% acoustic build, ~25% video and screen, ~20% audio, ~10% control and automation, ~10% seating and finishes.",
    readingMinutes: 7,
    publishedAt: "2026-02-14",
    popular: true,
    tags: ["Cost", "Home Cinema", "Budget"],
    visual: "Stacked bar by tier: Entry / Reference / Flagship, with line items.",
    example:
      "A flagship Atmos cinema we delivered came in at ₹1.85 cr — of which ₹68 lakh was structural acoustic prep done correctly during civil works.",
    lessons: [
      "Acoustic prep is non-negotiable.",
      "Don't compromise the screen size to save on projector.",
      "Calibrate after every major furniture change.",
    ],
    relatedProjects: ["Atmos cinema, Noida"],
    faq: [
      { q: "Cheapest serious tier?", a: "Around ₹35–45 lakh, scoped tight, one room, one purpose." },
      { q: "What blows the budget?", a: "Late-stage acoustic fixes." },
    ],
  },
  {
    slug: "smart-automation-versus-smart-gadgets",
    category: "smart-automation",
    question: "What's the difference between smart automation and smart gadgets?",
    shortAnswer:
      "Smart gadgets do one thing on their own. Smart automation is when many systems share state and act together — lighting reacts to occupancy, climate follows scenes, security follows mode, AV follows time of day. The user feels one building, not twelve apps.",
    readingMinutes: 6,
    publishedAt: "2026-02-01",
    recommended: true,
    tags: ["Automation", "Strategy", "UX"],
    visual: "Mode diagram: Morning / Day / Evening / Cinema / Away / Night — how every subsystem reacts.",
    example:
      "On a Gurugram penthouse we moved the family from 11 different apps to two scenes on the wall. Daily interactions went from ~70 taps to under 10.",
    lessons: [
      "Design modes before devices.",
      "Hide complexity behind scenes.",
      "Voice is a shortcut, not the primary UI.",
    ],
    relatedProjects: ["Penthouse, Gurugram"],
    faq: [
      { q: "Best platform?", a: "The one the integrator can service for 10 years." },
      { q: "Apple Home or Pro platforms?", a: "Pro platforms for villas and estates; consumer ecosystems for single-room use cases." },
    ],
  },
  {
    slug: "what-makes-an-auditorium-av-system-work",
    category: "audio-video",
    question: "What makes an auditorium AV system actually work?",
    shortAnswer:
      "Sightlines, intelligibility, and operator workflow — in that order. A flagship auditorium delivers consistent sound at every seat, readable visuals from the back row, and a control surface a non-engineer can run on event day.",
    readingMinutes: 8,
    publishedAt: "2026-01-20",
    tags: ["AV", "Auditorium", "Design"],
    visual: "Plan view: speaker coverage map, sightline cones, control booth ergonomics.",
    example:
      "An institutional auditorium of 720 seats was failing intelligibility tests in the rear third. We redesigned the line array, retuned the room and shortened the operator workflow from 14 steps to 4. Speech intelligibility scores moved into the 'excellent' band.",
    lessons: [
      "Speech intelligibility is the real KPI.",
      "Design for the volunteer operator, not the AV engineer.",
      "Lock the rigging early.",
    ],
    relatedProjects: ["720-seat auditorium, North India"],
    faq: [
      { q: "Line array or point source?", a: "Depends on seat count and room depth — both are correct in the right room." },
      { q: "How long does commissioning take?", a: "1–3 weeks for a serious auditorium." },
    ],
  },
  {
    slug: "lessons-from-1000-projects",
    category: "project-learnings",
    question: "What have 1000+ projects taught you?",
    shortAnswer:
      "Three things, repeatedly: brief the owner properly before the drawing, design the operating model before the spec, and over-invest in the cable plant. Everything else flows from those.",
    readingMinutes: 5,
    publishedAt: "2026-01-08",
    popular: true,
    tags: ["Lessons", "Strategy", "Field"],
    visual: "Timeline of a project: Brief → Design → Build → Commission → Operate — where most failures originate.",
    example:
      "A repeat client cut handover defects 70% after we moved a two-day briefing workshop to the front of every project.",
    lessons: [
      "Owners commission outcomes, not equipment.",
      "Operating model > BOM.",
      "Pathways, pathways, pathways.",
    ],
    relatedProjects: ["Multi-site corporate, North India"],
    faq: [
      { q: "Biggest avoidable cost?", a: "Re-doing pathways during interiors." },
      { q: "Biggest morale killer?", a: "Vendors arguing on site." },
    ],
  },
  {
    slug: "ai-search-and-intelligent-buildings",
    category: "technology-trends",
    question: "How will AI search change how owners buy intelligent buildings?",
    shortAnswer:
      "Owners will arrive with a synthesised, AI-shaped point of view before the first meeting. Vendors who can't explain themselves clearly to a model won't make it into the shortlist. Authority and clarity start to compound.",
    readingMinutes: 5,
    publishedAt: "2025-12-12",
    trending: true,
    tags: ["AI Search", "Strategy", "Future"],
    visual: "Funnel diagram: AI-mediated discovery → human shortlist → site visit.",
    example:
      "Three enterprise clients in the last quarter referenced AI-generated comparisons in their first call. The shortlist had already happened.",
    lessons: [
      "Publish like you're being read by a model.",
      "Authority compounds in the answer layer.",
      "Clarity beats keywords.",
    ],
    relatedProjects: [],
    faq: [
      { q: "Does SEO still matter?", a: "Yes — it's the substrate the answer layer is built on." },
      { q: "Should I write FAQs?", a: "Yes — that's what AI search actually consumes." },
    ],
  },
  {
    slug: "access-control-modern-stack",
    category: "security-infrastructure",
    question: "What does a modern access control stack look like?",
    shortAnswer:
      "Credentials on the phone, edge readers with strong cryptography, a cloud-managed policy plane, and visitor flows built into the same model. The badge is no longer the centre of gravity.",
    readingMinutes: 7,
    publishedAt: "2025-11-28",
    recommended: true,
    tags: ["Access Control", "Security", "Stack"],
    visual: "Stack: Phone credential → Reader → Controller → Policy plane → Audit.",
    example:
      "A corporate HQ moved 4,200 employees from cards to phone credentials in six weeks. Lost-credential tickets dropped 92%.",
    lessons: [
      "Phone is the new badge.",
      "Visitor flows belong in the same model.",
      "Audit is a feature, not an afterthought.",
    ],
    relatedProjects: ["Corporate HQ, NCR"],
    faq: [
      { q: "Are cards dead?", a: "Not yet — they remain a fallback." },
      { q: "Biometric?", a: "Use for high-trust zones, not the front door of every room." },
    ],
  },
  {
    slug: "mistakes-in-perimeter-security",
    category: "mistakes-to-avoid",
    question: "Most common mistakes in perimeter security projects?",
    shortAnswer:
      "Specifying sensors before deciding response protocol, treating the command centre as a furniture exercise, and skipping drills. The fence is the easy part.",
    readingMinutes: 6,
    publishedAt: "2025-11-10",
    tags: ["Perimeter", "Mistakes", "Operations"],
    visual: "Failure modes mapped against the perimeter timeline: detect → verify → decide → respond.",
    example:
      "A site with first-class hardware was missing intrusions because no one had rehearsed the response. A two-week drill cycle changed the numbers more than any equipment upgrade would have.",
    lessons: [
      "Protocol before sensors.",
      "Operators are part of the system.",
      "Drill, drill, drill.",
    ],
    relatedProjects: ["Critical infrastructure, NCR"],
    faq: [
      { q: "How often should we drill?", a: "Quarterly, minimum." },
      { q: "Who owns the drill?", a: "The client's security head — not the integrator." },
    ],
  },
  {
    slug: "cctv-planning-guide-for-architects",
    category: "security-infrastructure",
    question: "CCTV planning guide for architects: a pre-construction checklist",
    shortAnswer:
      "Plan CCTV the same week you plan electrical layouts. Locate cameras to cover entries, perimeter, vehicle paths and high-value zones, route cabling through structured pathways, and reserve a clean equipment room for the NVR/VMS. Decide retention, resolution and analytics before specifying any camera.",
    readingMinutes: 7,
    publishedAt: "2026-05-20",
    recommended: true,
    tags: ["CCTV", "Architects", "Planning", "Pre-construction"],
    visual:
      "Floor plate diagram showing camera coverage cones, blind spots, cable pathways and the dedicated equipment room — overlaid on a typical residential and office layout.",
    example:
      "On a 4-floor corporate fit-out, planning CCTV alongside the electrical drawings let us route all cabling inside the false ceiling on day one — no surface conduits, no chases cut into finished walls.",
    lessons: [
      "Plan camera locations against the architectural plan, not the BOQ.",
      "Reserve a clean, ventilated equipment room for NVR/VMS.",
      "Decide retention (days), resolution and analytics first; cameras second.",
      "Avoid wireless cameras for permanent installations.",
    ],
    relatedProjects: ["Corporate HQ, NCR", "Luxury residence, South Delhi"],
    faq: [
      { q: "Who should own the CCTV plan?", a: "The architect coordinates, but the security consultant signs off on camera locations, coverage and the equipment room." },
      { q: "How many cameras are 'enough'?", a: "It's coverage-driven, not count-driven. Cover entries, exits, perimeter, vehicle paths and high-value zones — gaps matter more than totals." },
    ],
  },
  {
    slug: "command-centre-design-essentials",
    category: "security-infrastructure",
    question: "Command centre design essentials: from video-wall to SOPs",
    shortAnswer:
      "A real command centre is a workflow, not a video-wall. Design the operator console, the escalation paths, the SOPs and the integration with field response first — then specify the displays, VMS and audio. The best ops rooms reduce time-to-decision, not screen real estate.",
    readingMinutes: 8,
    publishedAt: "2026-05-18",
    recommended: true,
    tags: ["Command Centre", "VMS", "Operations", "SOP"],
    visual:
      "Ops-room layout with operator sightlines, video-wall zones (overview / incident / map), escalation desk and the SOP decision tree on the side wall.",
    example:
      "A multi-site command centre we designed reduced false-positive escalations by 60% after we replaced a brand-new video-wall with a simple incident-classification SOP and two correctly-positioned operator consoles.",
    lessons: [
      "Design SOPs before specifying hardware.",
      "Operator sightlines and ergonomics matter more than display count.",
      "Integrate VMS, access, fire and PA into one alert pane.",
      "Plan operator shifts, drills and audit trails.",
    ],
    relatedProjects: ["Critical infrastructure, NCR", "Industrial command centre, North India"],
    faq: [
      { q: "Do we need a video-wall?", a: "Only if multiple operators must share the same picture. Many ops rooms run better on dual-monitor consoles plus one shared incident display." },
      { q: "On-prem or cloud VMS?", a: "For mission-critical sites, on-prem hardened VMS with off-site backup. Cloud-only is rarely acceptable for compliance and uptime." },
    ],
  },
  {
    slug: "access-control-vs-biometrics",
    category: "security-infrastructure",
    question: "Access control vs biometrics: which is right for your project?",
    shortAnswer:
      "Cards and mobile credentials win on speed, scale and visitor flow. Biometrics (face, fingerprint, iris) win on identity assurance for sensitive zones. Most real projects use both — cards for daily flow, biometrics for restricted doors — under one policy engine with full audit logging.",
    readingMinutes: 6,
    publishedAt: "2026-05-15",
    tags: ["Access Control", "Biometrics", "OSDP", "Identity"],
    visual:
      "Decision tree mapping door type → credential type → reader type → backend integration, with OSDP vs Wiegand and on-device vs server-side matching highlighted.",
    example:
      "An R&D facility deployed face recognition at lab doors and mobile credentials at general office floors, both governed by one policy engine — saving operations cost without weakening sensitive-zone identity assurance.",
    lessons: [
      "Match credential strength to the zone's risk profile.",
      "Specify OSDP, not Wiegand, for any new install.",
      "Keep enrolment and revocation workflows simple — they break in practice.",
      "Audit logging is the deliverable, not a feature.",
    ],
    relatedProjects: ["R&D facility, NCR", "Corporate HQ, Bengaluru"],
    faq: [
      { q: "Is face recognition reliable?", a: "Modern face systems work well in controlled lighting with quality optics. They struggle outdoors or with poor enrolment hygiene." },
      { q: "Can we mix brands?", a: "Yes — under an open access-control platform. Avoid being locked into one OEM's reader-and-controller stack." },
    ],
  },
  {
    slug: "fire-alarm-and-life-safety-checklist",
    category: "intelligent-buildings",
    question: "Fire alarm and life safety: a planning checklist for owners and architects",
    shortAnswer:
      "Life safety is non-negotiable, code-driven and easiest when planned early. Specify an addressable fire alarm system, a PAVA (public address / voice alarm) where occupancy warrants, integrate with HVAC dampers and access control fail-safe behaviour, and plan testing and drills from day one.",
    readingMinutes: 7,
    publishedAt: "2026-05-12",
    tags: ["Fire Alarm", "Life Safety", "PAVA", "NBC"],
    visual:
      "Building section showing addressable detector zones, PAVA speaker coverage, fire-rated cabling pathways and the fail-safe interlocks with HVAC, lifts and access control.",
    example:
      "On a mixed-use development, integrating fire alarm with access control fail-safe and HVAC dampers cut response time during a drill from 6 minutes to under 90 seconds.",
    lessons: [
      "Addressable, not conventional — for anything beyond a small footprint.",
      "PAVA where there are crowds; sirens alone aren't enough.",
      "Fire-rated cabling pathways must be reserved in design, not patched in later.",
      "Drills and documented evacuation maps are part of the deliverable.",
    ],
    relatedProjects: ["Mixed-use development, NCR", "Corporate campus, Bengaluru"],
    faq: [
      { q: "Who certifies the system?", a: "A licensed fire consultant signs off against the local fire NOC and NBC requirements; the integrator implements to that brief." },
      { q: "Does fire need to integrate with BMS?", a: "Yes — at minimum for HVAC damper control, lift recall and access control fail-safe behaviour." },
    ],
  },
  {
    slug: "boom-barrier-and-anpr-buyers-guide",
    category: "security-infrastructure",
    question: "Boom barrier and ANPR buyer's guide: what to actually specify",
    shortAnswer:
      "Specify boom barriers by duty cycle (operations per hour), not by brand sticker. Pair with ANPR cameras tuned for your plate format, lighting and approach angle. Integrate with visitor management, parking and dispatch so the gate isn't a standalone island.",
    readingMinutes: 6,
    publishedAt: "2026-05-08",
    tags: ["Boom Barrier", "ANPR", "Gate Automation", "Parking"],
    visual:
      "Gate plan-view: ANPR camera position, induction loops, boom barrier reach, pedestrian segregation and the back-office integration with visitor and parking systems.",
    example:
      "A logistics yard's under-specified boom barriers were failing every 6 weeks. Replacing with a high-cycle industrial barrier and tuning the ANPR approach geometry brought MTBF above 18 months.",
    lessons: [
      "Match barrier duty cycle to actual peak operations per hour.",
      "ANPR accuracy depends on geometry and lighting, not megapixels alone.",
      "Integrate gate with visitor, parking and dispatch — not a standalone PLC.",
      "Plan power, surge protection and manual override for every gate.",
    ],
    relatedProjects: ["Logistics yard, North India", "Corporate campus, NCR"],
    faq: [
      { q: "How fast should a boom barrier open?", a: "For high-traffic gates, 1.5–3 seconds full open; faster shortens lifespan." },
      { q: "Is ANPR enough on its own?", a: "No — pair with RFID or driver authentication for material-handling and dispatch gates." },
    ],
  },
  {
    slug: "perimeter-security-for-private-estates",
    category: "perimeter-protection",
    question: "Perimeter security for private estates and farmhouses",
    shortAnswer:
      "Long, partially-walled estates need layered detection — thermal cameras with AI analytics across the boundary, beam or fence sensors on vulnerable stretches, and verification cameras at gates. The goal is low false alarms and clear remote verification when the owner is away.",
    readingMinutes: 7,
    publishedAt: "2026-05-05",
    tags: ["Perimeter", "Private Estates", "Thermal", "Analytics"],
    visual:
      "Estate plan showing thermal coverage along the boundary, vulnerable-stretch fence sensors, gate verification cameras and the single owner-app feed.",
    example:
      "A 12-acre estate replaced 22 floodlit visible cameras with 6 thermal cameras and AI analytics, dropping nightly false alarms from 40+ to under 3 while extending verified detection range by 4x.",
    lessons: [
      "Walls are deterrence, not detection.",
      "Thermal + analytics beats more visible-light cameras on long boundaries.",
      "Verification cameras at gates, every time.",
      "One owner app, with verified caretaker access — not separate logins.",
    ],
    relatedProjects: ["Private estate, Delhi NCR", "Weekend farmhouse, North India"],
    faq: [
      { q: "Do we need a fence sensor everywhere?", a: "No — only on vulnerable, low-visibility stretches. Most of the boundary can be covered by thermal + analytics." },
      { q: "What about caretaker access?", a: "The same app with role-based permissions — caretakers see what they need, the owner sees everything, every action is logged." },
    ],
  },
  {
    slug: "intelligent-buildings-ibms-explained",
    category: "intelligent-buildings",
    question: "Intelligent buildings and IBMS, explained in plain English",
    shortAnswer:
      "An intelligent building is one where HVAC, lighting, security, fire, access and energy systems talk to each other under one operating model. IBMS (Integrated Building Management System) is the platform that ties them together — not just a dashboard, but a workflow and an operating model.",
    readingMinutes: 7,
    publishedAt: "2026-05-02",
    tags: ["IBMS", "BMS", "Smart Buildings", "Operations"],
    visual:
      "Stack diagram: field devices → controllers → BMS → IBMS integration layer → operations dashboards and analytics, with security, fire, access and energy as peer systems.",
    example:
      "A corporate campus moved from 4 disconnected dashboards (BMS, fire, access, CCTV) to a single IBMS workflow, cutting operator workload by 35% and reducing average incident response time from 8 to 3 minutes.",
    lessons: [
      "IBMS is an operating model, not a screen.",
      "Start with the operations workflow, then pick the platform.",
      "Open protocols (BACnet, Modbus, OPC, ONVIF) over closed OEM stacks.",
      "Energy analytics pays for itself within 18–24 months on most commercial buildings.",
    ],
    relatedProjects: ["Corporate campus, Bengaluru", "Mixed-use development, NCR"],
    faq: [
      { q: "Is BMS the same as IBMS?", a: "No. BMS controls HVAC and electrical. IBMS integrates BMS with security, fire, access, AV and energy under one operating model." },
      { q: "Do we need IBMS for a single building?", a: "If it has more than 2–3 major systems and a dedicated FM team, yes." },
    ],
  },
  {
    slug: "auditorium-av-planning-guide",
    category: "audio-video",
    question: "Auditorium AV planning guide: from acoustics to one-touch operation",
    shortAnswer:
      "Auditoriums succeed when acoustics, sightlines, AV and operations are designed together — not bolted on. Acoustic shell first, loudspeaker design from first principles, lecture-capture and streaming planned for from day one, and a single touch-panel UX so the room runs without a technician.",
    readingMinutes: 8,
    publishedAt: "2026-04-28",
    tags: ["Auditorium", "AV", "Acoustics", "Lecture Capture"],
    visual:
      "Hall section showing acoustic treatment zones, loudspeaker coverage, projection geometry, lecture-capture camera positions and the operator console at the rear.",
    example:
      "A 1,200-seat institutional hall had unintelligible speech in the back third. Re-modelling the room acoustics and re-designing the loudspeaker array (same vendor) brought STI above 0.60 across all seats.",
    lessons: [
      "Acoustics before any speaker selection.",
      "Loudspeaker design from coverage, not from a brand catalogue.",
      "Hybrid capture and streaming designed in, not added later.",
      "One-touch operation for non-technical users.",
    ],
    relatedProjects: ["1,200-seat institutional hall, North India", "Corporate auditorium, NCR"],
    faq: [
      { q: "How early should AV be involved?", a: "At architectural design — before column grids, ceiling heights and HVAC routing are frozen." },
      { q: "Do we need a control room?", a: "Yes — even small halls benefit from a dedicated, sightline-correct operator position." },
    ],
  },
  {
    slug: "home-cinema-acoustic-design-basics",
    category: "luxury-home-cinema",
    question: "Home cinema acoustic design: the basics that decide everything",
    shortAnswer:
      "A reference home cinema is 60% architecture, 30% acoustics and 10% gear. Get the room shape right, isolate from adjacent spaces, route HVAC around the cinema, and treat the room with the right balance of absorption and diffusion. Then specify speakers — not before.",
    readingMinutes: 8,
    publishedAt: "2026-04-25",
    recommended: true,
    tags: ["Home Cinema", "Acoustics", "Dolby Atmos", "Design"],
    visual:
      "Cinema section showing floating floor, decoupled walls, acoustic shell, isolated HVAC paths, 7.2.4 / 9.4.6 Atmos speaker layout and seat-to-screen geometry.",
    example:
      "A client's flagship-spec cinema sounded harsh until we added a properly tuned acoustic shell, isolated the ceiling and re-positioned seating. Same equipment — completely different room.",
    lessons: [
      "Shell and isolation before any speaker is chosen.",
      "Never let HVAC ducts pass directly through the cinema ceiling.",
      "Lock seat geometry to the screen and reference axis early.",
      "Calibration matters more than badges on the box.",
    ],
    relatedProjects: ["Private theatre, Lutyens Delhi", "Atmos cinema, Noida"],
    faq: [
      { q: "Do I need a dedicated room?", a: "For reference performance, yes. Open-plan media rooms trade fidelity for flexibility." },
      { q: "7.2.4 or 9.4.6 Atmos?", a: "9.4.6 if the room geometry supports it cleanly; 7.2.4 otherwise. Don't force a layout into a room that can't hold it." },
    ],
  },
  {
    slug: "how-to-choose-a-security-integrator",
    category: "mistakes-to-avoid",
    question: "How to choose a security and technology integrator (without regret)",
    shortAnswer:
      "Choose for engineering depth, documented process and long-term operations support — not the lowest BOQ. Ask for design drawings, commissioning protocols, SOPs and an honest AMC. The cheapest integrator is rarely the cheapest project over five years.",
    readingMinutes: 6,
    publishedAt: "2026-04-20",
    tags: ["Integrator", "Procurement", "AMC", "Selection"],
    visual:
      "Checklist comparing two integrators across: design drawings, commissioning protocol, SOP documentation, AMC structure, references and engineering depth — versus a lowest-bid spec sheet.",
    example:
      "A client picked the lowest bidder for a 200-camera deployment. Eighteen months later, half the cameras were offline, no SOP existed, and the rip-and-replace cost 2.4x the original 'savings'.",
    lessons: [
      "Ask for design drawings before commercial discussion.",
      "Insist on a documented commissioning and handover protocol.",
      "Evaluate AMC and operations support as part of the decision.",
      "References from 3-year-old projects, not just freshly-installed ones.",
    ],
    relatedProjects: ["Corporate campus rescue, NCR", "Mixed-use redeployment, North India"],
    faq: [
      { q: "Is brand or integrator more important?", a: "Integrator. A great integrator with a mid-tier brand outperforms a poor integrator with the best brand, every time." },
      { q: "What should an AMC actually cover?", a: "Preventive maintenance, response SLAs, spares stocking, firmware management and an annual operations review — not just break-fix calls." },
    ],
  },
  {
    slug: "cctv-storage-and-retention-planning",
    category: "security-infrastructure",
    question: "CCTV storage and retention: how to plan it without overspending",
    shortAnswer:
      "Storage is usually the second-largest line in a CCTV BOQ — and the most over-specified. Plan from three numbers: stream bitrate (Mbps per camera), retention days required by policy or regulator, and RAID overhead. Pair smart codecs (H.265, H.265+) with motion-based recording on low-activity zones to cut storage by 40–60% without losing forensic value.",
    readingMinutes: 6,
    publishedAt: "2026-05-18",
    tags: ["CCTV", "Storage", "NVR", "Retention", "H.265"],
    visual:
      "Worksheet: cameras × bitrate × hours × retention days ÷ codec efficiency, then RAID overhead and growth headroom — sized as NVR-attached, SAN or hybrid cloud cold storage.",
    example:
      "A 240-camera campus was quoted 1.2 PB for 30-day retention. Re-engineering with H.265+ smart codec, motion-based recording on parking and corridors, and a tiered hot/warm/cold storage plan brought the requirement to 410 TB — at the same forensic quality.",
    lessons: [
      "Bitrate, not megapixels, drives storage. Tune VBR per zone.",
      "H.265+ and smart codecs cut storage 40–60% versus H.264.",
      "Tier storage: hot NVR, warm SAN, cold archive for incident retention.",
      "Plan RAID overhead and 20% growth headroom into the spec, not after.",
      "Match retention days to policy or regulator — over-retention is wasted money and a privacy risk.",
    ],
    relatedProjects: ["Corporate campus, Bengaluru", "Logistics yard, North India"],
    faq: [
      { q: "How many days of retention do we really need?", a: "Most enterprises plan 30 days hot, 90 days warm; regulated industries (banking, critical infrastructure) extend to 180–365 days on tiered storage." },
      { q: "NVR, SAN or cloud?", a: "Hot recording stays on NVRs near cameras. Warm/cold tiers go to SAN or object storage. Cloud is right for cold archive and disaster-recovery copies, not primary recording." },
    ],
  },
  {
    slug: "cctv-installation-cost-india",
    category: "cost-guides",
    question: "How much does CCTV installation cost in India?",
    shortAnswer:
      "CCTV installation cost in India depends on camera count, resolution, night coverage, storage days, cabling, network work and commissioning. A useful quote separates cameras and recording from civil, electrical, integration and AMC costs instead of offering one unexplained package price.",
    readingMinutes: 6,
    publishedAt: "2026-09-01",
    trending: true,
    tags: ["CCTV", "Installation Cost", "India", "Budget"],
    visual:
      "CCTV budget worksheet for a home, office, warehouse and campus, split into cameras, cabling, storage, installation, integration and AMC.",
    example:
      "For a multi-zone warehouse, the camera count was not the largest decision. Dock lighting, cable routes, retention days and the need to identify faces or vehicle plates changed the proposal more than the headline camera price.",
    lessons: [
      "Specify the evidence each camera must capture before choosing resolution.",
      "Separate equipment, site work, commissioning and AMC in every quote.",
      "Include storage, UPS, network and cybersecurity in the first budget.",
      "Test night scenes and handover footage, not only daytime live view.",
    ],
    relatedProjects: ["Warehouse surveillance, North India", "Corporate campus, Bengaluru"],
    faq: [
      { q: "What changes CCTV installation cost most in India?", a: "Camera count, lens and low-light requirements, storage retention, cable distance, civil work, network infrastructure and the level of integration usually matter most." },
      { q: "Should AMC be included in the initial CCTV quote?", a: "Yes. Ask for preventive visits, response times, spares, firmware updates, health checks and clear exclusions so lifecycle cost can be compared." },
    ],
  },
  {
    slug: "anpr-camera-price-india",
    publishedAt: "2026-09-01",
    category: "security-infrastructure",
    question: "What affects ANPR camera price in India?",
    shortAnswer:
      "ANPR camera price in India is shaped by capture distance, lane speed, lighting, plate format, accuracy requirements, edge analytics, software licensing, barriers and integration. Compare a complete lane design rather than the camera unit alone.",
    readingMinutes: 6,
    tags: ["ANPR", "Vehicle Access", "India", "Cameras"],
    visual:
      "ANPR lane design showing camera position, IR illumination, barrier, loop or radar trigger, whitelist workflow and access-control integration.",
    example:
      "A busy commercial gate needed reliable reads at night and during queueing. Repositioning the camera, controlling the lane geometry and adding a trigger improved the result more than upgrading the sensor in isolation.",
    lessons: [
      "Design the lane geometry and plate angle before comparing models.",
      "Check Indian plate formats, speed and night conditions in the acceptance test.",
      "Clarify whether analytics and software licences are one-time or recurring.",
      "Integrate ANPR events with barriers, visitor workflows and audit logs.",
    ],
    relatedProjects: ["Managed estate gate, Gurugram", "Industrial campus, Western India"],
    faq: [
      { q: "Is the lowest ANPR camera price the best value?", a: "Not usually. Accuracy, lane design, illumination, software, integration and support determine whether the system works reliably at the gate." },
      { q: "Can ANPR work in Indian night conditions?", a: "Yes, when camera position, shutter settings, IR illumination, lane control and commissioning tests are designed for the actual approach." },
    ],
  },
  {
    slug: "perimeter-intrusion-detection-system-india",
    publishedAt: "2026-09-01",
    category: "perimeter-protection",
    question: "How does a perimeter intrusion detection system work in India?",
    shortAnswer:
      "A perimeter intrusion detection system in India combines a physical boundary, sensors, zone controllers, CCTV verification, alarm software and a response plan. The right design reduces nuisance alarms while giving operators a precise location and an actionable event.",
    readingMinutes: 7,
    tags: ["PIDS", "Perimeter Security", "India", "Detection"],
    visual:
      "Layered PIDS layout for an Indian industrial site: fence sensor zones, fibre or microwave detection, PTZ verification, control room map and response route.",
    example:
      "On a long industrial boundary, dividing the line into meaningful zones and pairing each alarm with a verification view gave the security team enough context to act without watching hundreds of camera feeds continuously.",
    lessons: [
      "Start with threat zones, terrain, vegetation and response time.",
      "Pair detection with verification to control nuisance alarms.",
      "Show alarm location and escalation status on one operator workflow.",
      "Measure detection, verification and response during acceptance tests.",
    ],
    relatedProjects: ["38 km industrial perimeter, Western India", "Critical infrastructure, North India"],
    faq: [
      { q: "Is PIDS the same as CCTV?", a: "No. PIDS detects or reports a perimeter event; CCTV helps operators verify and assess it. Effective sites use the two together." },
      { q: "Where is PIDS used in India?", a: "Common applications include factories, logistics yards, utilities, campuses, estates and other sites where early warning along the boundary matters." },
    ],
  },
  {
    slug: "command-control-room-design-india",
    publishedAt: "2026-09-01",
    category: "audio-video",
    question: "What should a command control room design include in India?",
    shortAnswer:
      "A command control room design in India should align operator sightlines, display purpose, acoustics, lighting, ergonomics, power, cooling, network resilience, alarm workflows and future expansion. A video wall alone does not make a control room operational.",
    readingMinutes: 6,
    tags: ["Command Centre", "Control Room", "India", "AV"],
    visual:
      "Control room plan showing operator sightlines, video wall viewing zones, acoustic treatment, resilient power, cooling, collaboration and supervisor positions.",
    example:
      "A control room refresh improved response by clarifying which information belonged on shared displays, which belonged at each operator position and how an incident moved from alarm to acknowledgement to escalation.",
    lessons: [
      "Design workflows and sightlines before selecting the video wall.",
      "Keep critical alarms legible without overloading the shared display.",
      "Plan UPS, cooling, network paths and service access as core scope.",
      "Commission the room with real operator scenarios and shift feedback.",
    ],
    relatedProjects: ["Security command centre, India", "Integrated operations room, Delhi NCR"],
    faq: [
      { q: "What is the most important part of a command control room?", a: "The operator workflow. Displays, furniture, AV, acoustics and systems should help people detect, understand and respond to events without unnecessary cognitive load." },
      { q: "Does every control room need a video wall?", a: "No. Use a video wall when shared situational awareness justifies it; smaller teams may work better with carefully designed operator displays and a smaller shared screen." },
    ],
  },
  {
    slug: "fire-alarm-system-price-india",
    publishedAt: "2026-09-01",
    category: "security-infrastructure",
    question: "What determines fire alarm system price in India?",
    shortAnswer:
      "Fire alarm system price in India depends on the building size, risk classification, detector and notification count, loop architecture, cause-and-effect logic, cabling, commissioning and statutory requirements. A compliant design should be priced from drawings and an approved sequence, not detector count alone.",
    readingMinutes: 6,
    tags: ["Fire Alarm", "Life Safety", "India", "Cost Guide"],
    visual:
      "Fire alarm scope map covering detection loops, manual call points, sounders, interfaces, fire command centre, cabling, testing and handover documents.",
    example:
      "For a mixed-use building, the cause-and-effect matrix exposed more scope than the device schedule: lifts, dampers, pumps, access-controlled doors, smoke control and occupant notification all needed coordinated testing.",
    lessons: [
      "Begin with the approved fire strategy and cause-and-effect matrix.",
      "Price interfaces and commissioning, not only panels and detectors.",
      "Coordinate routes, fire-rated cabling and access for maintenance early.",
      "Document every test result and train the people who receive alarms.",
    ],
    relatedProjects: ["Commercial building, Delhi NCR", "Institutional campus, India"],
    faq: [
      { q: "What is included in a fire alarm quotation?", a: "A proper quotation covers the panel, detection and notification devices, modules, cabling, interfaces, installation, programming, testing, documentation and training, subject to the approved design." },
      { q: "Can fire alarm system price be compared by detector count?", a: "Detector count is only one input. Building risk, loop length, interfaces, notification, cabling, commissioning and authority requirements can change the installed scope substantially." },
    ],
  },
];

export function getArticle(slug: string) {
  return ASK_ARIF_ARTICLES.find((a) => a.slug === slug);
}

export function getCategory(slug: string) {
  return ASK_ARIF_CATEGORIES.find((c) => c.slug === slug);
}

export function articlesByCategory(slug: string) {
  return ASK_ARIF_ARTICLES.filter((a) => a.category === slug);
}

export function relatedArticles(slug: string, count = 3) {
  const article = getArticle(slug);
  if (!article) return [];
  return ASK_ARIF_ARTICLES
    .filter((a) => a.slug !== slug)
    .sort((a, b) => {
      const sameCat = (x: AskArifArticle) => (x.category === article.category ? 1 : 0);
      return sameCat(b) - sameCat(a);
    })
    .slice(0, count);
}

/**
 * Shorter, search-ready titles for questions whose natural phrasing would be
 * truncated in the SERP once the "| Ask Arif" suffix is appended. Keep each
 * entry at or under 48 characters so the branded title stays within 60.
 */
export const ASK_ARIF_META_TITLES: Record<string, string> = {
  "fire-alarm-and-life-safety-checklist": "Fire Alarm & Life Safety Planning Checklist",
  "how-to-choose-a-security-integrator": "How to Choose a Security Integrator",
  "auditorium-av-planning-guide": "Auditorium AV Planning Guide",
  "smart-automation-versus-smart-gadgets": "Smart Automation vs Smart Gadgets",
  "cctv-planning-guide-for-architects": "CCTV Planning Guide for Architects",
  "ai-search-and-intelligent-buildings": "AI Search & Intelligent Buildings",
  "cctv-storage-and-retention-planning": "CCTV Storage & Retention Planning",
  "home-cinema-acoustic-design-basics": "Home Cinema Acoustic Design Basics",
  "access-control-vs-biometrics": "Access Control vs Biometrics",
  "boom-barrier-and-anpr-buyers-guide": "Boom Barrier & ANPR Buyer's Guide",
  "intelligent-buildings-ibms-explained": "Intelligent Buildings & IBMS Explained",
  "command-centre-design-essentials": "Command Centre Design Essentials",
  "common-mistakes-in-home-theatre-projects": "Luxury Home Theatre Mistakes to Avoid",
  "perimeter-security-for-private-estates": "Perimeter Security for Private Estates",
  "mistakes-in-perimeter-security": "Perimeter Security Mistakes to Avoid",
  "access-control-modern-stack": "The Modern Access Control Stack",
  "what-makes-an-auditorium-av-system-work": "What Makes Auditorium AV Work",
  "cctv-installation-cost-india": "CCTV Installation Cost in India",
  "anpr-camera-price-india": "ANPR Camera Price in India",
  "perimeter-intrusion-detection-system-india": "Perimeter Intrusion Detection India",
  "command-control-room-design-india": "Command Control Room Design India",
  "fire-alarm-system-price-india": "Fire Alarm System Price India",
};

/** Title used in <title> / og:title — the short form when one exists. */
export function articleMetaTitle(article: AskArifArticle): string {
  return ASK_ARIF_META_TITLES[article.slug] ?? article.question;
}
